import { LocationModel, LocationTypes, PlantsEnum, BOMModel } from '@cpak/adp-cpak-quotes-service-models';
import { AttributeTypeIds, ProductModel, AttributeModel } from '@cpak/adp-cpak-products-service-models';
import {
  checkSecondLocationIsAvailable,
  getAttributeValue,
  getLocationIdsFromBom,
  getProductAllowedLocations,
  getProductCountryInclusion,
  getProductCountryStatusCode,
  getProductDefaultLocation,
  getProductManufacturingPlantStatus,
} from './utils';
import { FALSE_PRODUCT_ATTRIBUTE_VALUES, productAvailableCodes } from '../../consts';

export function getProductIdsWithoutLocations(
  products: ProductModel[],
  locations: LocationModel[],
  isSisterOrLocalPack: boolean,
  bom: BOMModel,
  plant: PlantsEnum,
  countryId: string
): string[] {
  return products
    .filter(
      product =>
        !getProductLocationForPack({ product, plant, countryId, locations, isSisterPack: isSisterOrLocalPack, bom })
    )
    .map(product => product.id);
}

export function getLocationTypeToFilterBy(
  locations: LocationModel[] = [],
  isSisterPack: boolean
): LocationTypes | undefined {
  if (locations.length === 0) {
    return undefined;
  }

  const locationTypes = new Set(locations.map(location => location.type));

  if (isSisterPack) {
    locationTypes.delete(LocationTypes.main);
  }

  return locationTypes.size === 1 ? [...locationTypes][0] : undefined;
}

export function getProductLocations(
  attributes: AttributeModel[],
  locations: LocationModel[],
  plant: PlantsEnum
): LocationModel[] {
  return getProductAllowedLocations(attributes, plant)
    .map(locationId => locations.find(location => location.id === locationId))
    .filter((location): location is LocationModel => !!location);
}

export function getAvailableProductLocations(data: {
  attributes: AttributeModel[];
  locations: LocationModel[];
  countryId: string;
  plant: PlantsEnum;
  isSisterPack?: boolean;
}): LocationModel[] {
  const { attributes, locations, isSisterPack, countryId, plant } = data;

  const allowedLocations = getProductLocations(attributes, locations, plant);

  const allowedLocationTypes = new Set();

  const countryInclusion: boolean = getProductCountryInclusion({ attributes, countryId });
  const countryStatus: number | null = getProductCountryStatusCode(attributes, countryId);

  const manufacturingPlantStatus: number | null = getProductManufacturingPlantStatus({
    attributes: attributes,
    plant,
  });

  /* for add after check (Country status = 50 or 60) */
  if (countryStatus && productAvailableCodes.has(countryStatus)) {
    allowedLocationTypes.add(LocationTypes.addAfter);
  }

  /* for main check that not sister pack and
    Country exists in Country Inclusion table for the selected part and
  MFG Plant status = 50 or 60 */
  if (
    !isSisterPack &&
    countryInclusion &&
    manufacturingPlantStatus &&
    productAvailableCodes.has(manufacturingPlantStatus)
  ) {
    allowedLocationTypes.add(LocationTypes.main);
  }

  return allowedLocations.filter(location => allowedLocationTypes.has(location.type));
}

export function getProductLocationForPack(data: {
  product: ProductModel;
  plant: PlantsEnum;
  countryId: string | undefined;
  locations: LocationModel[];
  bom: BOMModel | undefined;
  isSisterPack?: boolean;
  partForReplacementLocationId?: string | null;
}): LocationModel | null {
  const {
    product,
    plant,
    countryId,
    locations,
    isSisterPack,
    bom,
    partForReplacementLocationId: replacementLocationId,
  } = data;

  if (!bom || !countryId || locations.length === 0) return null;

  // check if product quote type matches pack in focus quote type
  const productTypeAttribute = product.attributes.find(
    attr => attr.attributeTypeId === AttributeTypeIds.QuoteProductType
  );
  const productVisibleAttribute = product.attributes.find(attr => attr.attributeTypeId === AttributeTypeIds.Visible);

  if (!productTypeAttribute || !productVisibleAttribute) return null;

  const productQuoteProductTypes = getAttributeValue(productTypeAttribute, plant);
  const productVisibleAttrValue = getAttributeValue(productVisibleAttribute, plant);

  if (
    typeof productVisibleAttrValue === 'string' &&
    FALSE_PRODUCT_ATTRIBUTE_VALUES.includes(productVisibleAttrValue.toLowerCase())
  ) {
    return null;
  }

  if (typeof productQuoteProductTypes === 'string' && productQuoteProductTypes !== bom.quoteProductId) return null;

  if (Array.isArray(productQuoteProductTypes) && !productQuoteProductTypes.includes(bom.quoteProductId)) return null;

  const takenBomLocations = getLocationIdsFromBom(bom);

  const defaultLocationId = getProductDefaultLocation(product.attributes, plant);

  if (!defaultLocationId) {
    return null;
  }

  // filter out locations that are not available locations and 'second' location if it's corresponding first location is not taken
  const availableLocations = getAvailableProductLocations({
    attributes: product.attributes,
    locations,
    isSisterPack,
    countryId,
    plant,
  }).filter(location => checkSecondLocationIsAvailable(location.id, takenBomLocations));

  const defaultLocation = availableLocations.find(location => location.id === defaultLocationId);

  const replacementLocation = availableLocations.find(location => location.id === replacementLocationId);

  return replacementLocation || defaultLocation || availableLocations[0] || null;
}
